import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, LogOut, Search, ExternalLink, Users, FolderKanban, ChevronUp, ChevronDown, Image as ImageIcon, Building2 } from 'lucide-react';
import ProjectModal from './ProjectModal';
import TeamEditor from './TeamEditor';
import HomepageEditor from './HomepageEditor';
import StudioEditor from './StudioEditor';
import { formatEmployeeExperience } from '../utils/employee';
import '../styles/admin/admin-dashboard.css';

const PROJECT_TYPES = [
  { id: 'all', label: 'Todos' },
  { id: 'residential', label: 'Residencial' },
  { id: 'commercial', label: 'Comercial' },
  { id: 'expography', label: 'Expografia' },
];

const typeLabel = (sector) => ({
  residential: 'Residencial',
  commercial: 'Comercial',
  expography: 'Expografia',
  museum: 'Expografia',
}[sector] || sector || '—');

export default function AdminDashboard({ onLogout, onNavigate }) {
  const [activeTab, setActiveTab] = useState('projects');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [editingProject, setEditingProject] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [employeeOrder, setEmployeeOrder] = useState(null);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');

  const queryClient = useQueryClient();

  const { data: projectsData } = useQuery({
    queryKey: ['admin-projects'],
    queryFn: async () => {
      const res = await fetch('/api/projects?status=all', {
        credentials: 'include',
        cache: 'no-store',
      });
      return res.json();
    }
  });

  const { data: teamData } = useQuery({
    queryKey: ['admin-employees'],
    queryFn: async () => {
      const res = await fetch('/api/employees?include_inactive=true', {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('Não foi possível carregar a equipe.');
      return res.json();
    }
  });

  const { data: settingsData, error: settingsError } = useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const res = await fetch('/api/settings', { credentials: 'include', cache: 'no-store' });
      if (!res.ok) throw new Error('Não foi possível carregar as configurações do site.');
      return res.json();
    },
  });

  const projects = projectsData?.projects || [];
  const employees = teamData?.employees || [];
  const activeEmployeeCount = employees.filter((employee) => employee.is_active !== 0).length;
  const orderedEmployees = useMemo(() => {
    if (!employeeOrder) return employees;
    const positions = new Map(employeeOrder.map((id, index) => [id, index]));
    return [...employees].sort((a, b) => (positions.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (positions.get(b.id) ?? Number.MAX_SAFE_INTEGER));
  }, [employees, employeeOrder]);

  useEffect(() => {
    if (employeeOrder === null && teamData?.employees) {
      setEmployeeOrder(teamData.employees.map((employee) => employee.id));
    }
  }, [employeeOrder, teamData]);

  useEffect(() => {
    if (!editingEmployee && orderedEmployees.length) setEditingEmployee(orderedEmployees[0]);
  }, [editingEmployee, orderedEmployees]);

  const publishedCount = projects.filter((project) => project.status === 'published').length;
  const typeCounts = useMemo(() => Object.fromEntries(
    PROJECT_TYPES.map(({ id }) => [id, id === 'all' ? projects.length : projects.filter((project) => project.sector === id || (id === 'expography' && project.sector === 'museum')).length])
  ), [projects]);
  const filteredProjects = projects.filter((project) => {
    const matchesType = activeType === 'all' || project.sector === activeType || (activeType === 'expography' && project.sector === 'museum');
    const searchableText = `${project.title || ''} ${project.city || ''} ${typeLabel(project.sector)} ${project.year || ''} ${project.summary || ''}`.toLocaleLowerCase();
    return matchesType && searchableText.includes(searchQuery.trim().toLocaleLowerCase());
  });

  const handleSaveProject = async (projectData) => {
    const isEdit = !!projectData.id;
    const url = isEdit ? `/api/projects/${projectData.id}` : '/api/projects';
    const method = isEdit ? 'PUT' : 'POST';

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(projectData)
    });

    if (res.ok) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-projects'] }),
        queryClient.invalidateQueries({ queryKey: ['project'] }),
      ]);
      setEditingProject(null);
      setIsCreatingProject(false);
    } else {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Erro ao salvar projeto.');
    }
  };

  const handleDeleteProject = async (id) => {
    if (!confirm('Deseja realmente excluir este projeto?')) return;
    const res = await fetch(`/api/projects/${id}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-projects'] }),
        queryClient.invalidateQueries({ queryKey: ['project'] }),
      ]);
      setEditingProject(null);
      return true;
    }
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Erro ao excluir projeto.');
  };

  const handleSaveEmployee = async (empData) => {
    const isEdit = !!empData.id;
    const url = isEdit ? `/api/employees/${empData.id}` : '/api/employees';
    const method = isEdit ? 'PUT' : 'POST';

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(empData)
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao salvar pessoa.');
    const savedEmployee = { ...empData, id: data.id || empData.id };
    if (!empData.id) setEmployeeOrder((current) => [...(current || employees.map((employee) => employee.id)), savedEmployee.id]);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['employees'] }),
      queryClient.invalidateQueries({ queryKey: ['admin-employees'] }),
    ]);
    return savedEmployee;
  };

  const handleDeleteEmployee = async (id) => {
    const employee = employees.find((item) => item.id === id);
    if (!confirm(`Deseja realmente excluir ${employee?.name || 'esta pessoa'}?`)) return false;
    const res = await fetch(`/api/employees/${id}`, { method: 'DELETE', credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao excluir pessoa.');
    setEmployeeOrder((current) => current?.filter((employeeId) => employeeId !== id) || null);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['employees'] }),
      queryClient.invalidateQueries({ queryKey: ['admin-employees'] }),
    ]);
    const remaining = queryClient.getQueryData(['admin-employees'])?.employees || [];
    setEditingEmployee(remaining[0] || null);
    return true;
  };

  const moveEmployee = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= orderedEmployees.length) return;
    const nextOrder = orderedEmployees.map((employee) => employee.id);
    [nextOrder[index], nextOrder[target]] = [nextOrder[target], nextOrder[index]];
    setEmployeeOrder(nextOrder);
    setOrderDirty(true);
    setOrderError('');
  };

  const saveEmployeeOrder = async () => {
    setSavingOrder(true);
    setOrderError('');
    try {
      const response = await fetch('/api/employees/order', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ employee_ids: orderedEmployees.map((employee) => employee.id) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Não foi possível salvar a ordem.');
      setOrderDirty(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['employees'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-employees'] }),
      ]);
    } catch (error) {
      setOrderError(error.message || 'Não foi possível salvar a ordem.');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleSaveHomepageImage = async (imageUrl) => {
    const response = await fetch('/api/settings/homepage-hero', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ homepage_hero_image: imageUrl }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Não foi possível salvar a imagem.');
    await queryClient.invalidateQueries({ queryKey: ['site-settings'] });
  };

  const handleSaveStudio = async (studio) => {
    const response = await fetch('/api/settings/homepage-studio', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ studio }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Não foi possível salvar a seção Estúdio.');
    await queryClient.invalidateQueries({ queryKey: ['site-settings'] });
  };

  return (
    <div className="admin">
      {/* Sidebar Navigation */}
      <aside className="admin__sidebar">
        <div className="font-display admin__brand">
          AURORA PAINEL
        </div>
        <nav className="admin__nav" aria-label="Painel">
          <button
            onClick={() => setActiveTab('projects')}
            className={`font-label admin__nav-link ${activeTab === 'projects' ? 'is-active' : ''}`}
            aria-current={activeTab === 'projects' ? 'page' : undefined}
          >
            <FolderKanban size={16} /> Projetos ({projects.length})
          </button>
          <button
            onClick={() => setActiveTab('team')}
            className={`font-label admin__nav-link ${activeTab === 'team' ? 'is-active' : ''}`}
            aria-current={activeTab === 'team' ? 'page' : undefined}
          >
            <Users size={16} /> Equipe ({employees.length})
          </button>
          <button
            onClick={() => setActiveTab('homepage')}
            className={`font-label admin__nav-link ${activeTab === 'homepage' ? 'is-active' : ''}`}
            aria-current={activeTab === 'homepage' ? 'page' : undefined}
          >
            <ImageIcon size={16} /> Página inicial
          </button>
          <button
            onClick={() => setActiveTab('studio')}
            className={`font-label admin__nav-link ${activeTab === 'studio' ? 'is-active' : ''}`}
            aria-current={activeTab === 'studio' ? 'page' : undefined}
          >
            <Building2 size={16} /> Estúdio
          </button>
        </nav>

        <div className="admin__sidebar-footer">
          <button
            onClick={() => onNavigate('/')}
            className="font-label admin__footer-link"
          >
            <ExternalLink size={14} /> Ver site
          </button>
          <button
            onClick={onLogout}
            className="font-label admin__footer-link admin__footer-link--danger"
          >
            <LogOut size={14} /> Sair
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="admin__main">
        {activeTab === 'projects' && (
          <>
            <div className="admin__header">
              <div>
                <h1 className="font-display admin__title">Projetos</h1>
                <p className="admin__subtitle">{projects.length} {projects.length === 1 ? 'projeto' : 'projetos'}, {publishedCount} {publishedCount === 1 ? 'publicado' : 'publicados'}.</p>
              </div>
              <button
                onClick={() => setIsCreatingProject(true)}
                className="btn btn--solid admin__cta"
              >
                <Plus size={16} /> Novo projeto
              </button>
            </div>

            <div className="admin__toolbar">
              <div className="admin-type-filters" role="group" aria-label="Filtrar por tipo">
                {PROJECT_TYPES.map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    className="admin-type-filter"
                    aria-pressed={activeType === id}
                    onClick={() => setActiveType(id)}
                  >
                    {label} <span>{typeCounts[id]}</span>
                  </button>
                ))}
              </div>
              <label className="admin-search">
                <Search size={16} className="admin-search__icon" />
                <input
                  type="search"
                  placeholder="Buscar projeto"
                  aria-label="Buscar projeto"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="admin-search__input"
                />
              </label>
            </div>

            {/* Projects Table */}
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr className="admin-table__head-row">
                    <th className="font-label admin-table__th">Capa</th>
                    <th className="font-label admin-table__th">Projeto</th>
                    <th className="font-label admin-table__th">Setor</th>
                    <th className="font-label admin-table__th">Ano</th>
                    <th className="font-label admin-table__th">Status</th>
                    <th className="font-label admin-table__th">Destaque</th>
                    <th className="font-label admin-table__th admin-table__th--actions">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map(proj => (
                    <tr key={proj.id} className="admin-table__row">
                      <td className="admin-table__cell">
                        {proj.cover_image ? <img src={proj.cover_image} alt="" className="admin-table__thumb" /> : <span className="admin-table__no-cover">Sem capa</span>}
                      </td>
                      <td className="admin-table__cell">
                        <strong className="font-display admin-table__project-title">{proj.title}</strong>
                        <span className="admin-table__project-summary">{[proj.city, proj.summary].filter(Boolean).join(' · ') || '—'}</span>
                      </td>
                      <td className="font-label admin-table__cell admin-table__sector">{typeLabel(proj.sector)}</td>
                      <td className="admin-table__cell">{proj.year || (proj.created_at ? new Date(proj.created_at).getFullYear() : '—')}</td>
                      <td className="admin-table__cell">
                        <span className={`font-label status-badge ${proj.status === 'published' ? 'status-badge--published' : ''}`}>
                          {proj.status === 'published' ? 'Publicado' : 'Rascunho'}
                        </span>
                      </td>
                      <td className="admin-table__cell">
                        {proj.featured ? <span className="font-label featured-badge">Destaque</span> : <span className="admin-empty-value">—</span>}
                      </td>
                      <td className="admin-table__cell admin-table__cell--actions">
                        <div className="admin-table__actions">
                          <button type="button" aria-label={`Editar ${proj.title}`} onClick={() => setEditingProject(proj)} className="btn btn--icon"><Edit2 size={14} /></button>
                          <button type="button" aria-label={`Excluir ${proj.title}`} onClick={() => handleDeleteProject(proj.id)} className="btn btn--icon btn--icon-danger"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredProjects.length === 0 && (
                    <tr><td colSpan="7" className="admin-empty-row">Nenhum projeto encontrado.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'team' && (
          <>
            <div className="admin__header">
              <div>
                <h1 className="font-display admin__title">Equipe</h1>
                <p className="admin__subtitle">{employees.length} {employees.length === 1 ? 'pessoa' : 'pessoas'}, {activeEmployeeCount} no site.</p>
              </div>
              <button
                onClick={() => setEditingEmployee({ id: null, name: '', role: '', bio: '', photo_url: '', employment_type: 'permanent', experience_level: '', years_experience: null, is_active: 1 })}
                className="btn btn--solid admin__cta"
              >
                <Plus size={16} /> Adicionar pessoa
              </button>
            </div>

            <div className="admin-team-layout">
              <div className="admin-team-list-column">
                <p className="admin-team-list-hint">A ordem da lista define a ordem dos cards no site. Use as setas para reorganizar e salve a ordem ao terminar.</p>
                {orderedEmployees.length === 0 ? (
                  <p className="admin-team-empty">Ninguém cadastrado ainda. Adicione uma pessoa para começar.</p>
                ) : (
                  <div className="admin-team-list">
                    {orderedEmployees.map((emp, index) => (
                      <article key={emp.id} className={`admin-team-card ${editingEmployee?.id === emp.id ? 'is-selected' : ''} ${emp.is_active === 0 ? 'is-inactive' : ''}`}>
                        <div className="admin-team-card__order">
                          <button type="button" aria-label={`Mover ${emp.name} para cima`} disabled={index === 0} onClick={() => moveEmployee(index, -1)}><ChevronUp size={17} /></button>
                          <button type="button" aria-label={`Mover ${emp.name} para baixo`} disabled={index === orderedEmployees.length - 1} onClick={() => moveEmployee(index, 1)}><ChevronDown size={17} /></button>
                        </div>
                        {emp.photo_url ? <img src={emp.photo_url} alt="" className="team-portrait admin-team-card__photo" /> : <span className="admin-team-card__no-photo">Sem foto</span>}
                        <div className="admin-team-card__identity">
                          <h3 className="font-display admin-team-card__name">{emp.name}</h3>
                          <p className="admin-team-card__role">{emp.role || 'Função não informada'}</p>
                          <p className="admin-team-card__experience">{formatEmployeeExperience(emp)}</p>
                          <span className="admin-team-card__employment">{emp.employment_type === 'permanent' ? 'Equipe permanente' : 'Colaboradora por projeto'}</span>
                        </div>
                        <span className={`font-label admin-team-card__status ${emp.is_active === 0 ? '' : 'is-active'}`}>{emp.is_active === 0 ? 'Inativa' : 'No site'}</span>
                        <button type="button" className="btn btn--outline admin-team-card__edit" onClick={() => setEditingEmployee(emp)}>Editar</button>
                      </article>
                    ))}
                  </div>
                )}
                <div className="admin-team-order-actions">
                  {orderError && <p className="admin-team-order-error" role="alert">{orderError}</p>}
                  {orderDirty && <button type="button" className="btn btn--solid" onClick={saveEmployeeOrder} disabled={savingOrder}>{savingOrder ? 'Salvando ordem...' : 'Salvar ordem'}</button>}
                </div>
              </div>

              {editingEmployee && (
                <TeamEditor
                  key={editingEmployee.id || 'new-person'}
                  employee={editingEmployee}
                  order={editingEmployee.id
                    ? (editingEmployee.sort_order ?? orderedEmployees.findIndex((employee) => employee.id === editingEmployee.id))
                    : Math.max(-1, ...employees.map((employee) => Number(employee.sort_order) || 0)) + 1}
                  onSave={handleSaveEmployee}
                  onDelete={handleDeleteEmployee}
                  onSaved={setEditingEmployee}
                />
              )}
            </div>
          </>
        )}

        {activeTab === 'homepage' && (
          <>
            <div className="admin__header">
              <div>
                <h1 className="font-display admin__title">Página inicial</h1>
                <p className="admin__subtitle">Gerencie a imagem de abertura do site.</p>
              </div>
            </div>
            {settingsError
              ? <p className="admin-homepage-editor__message is-error" role="alert">{settingsError.message}</p>
              : settingsData
                ? <HomepageEditor imageUrl={settingsData.homepage_hero_image || ''} onSave={handleSaveHomepageImage} />
                : <p className="admin-homepage-editor__loading">Carregando configurações...</p>}
          </>
        )}

        {activeTab === 'studio' && (
          <>
            <div className="admin__header">
              <div>
                <h1 className="font-display admin__title">Estúdio</h1>
                <p className="admin__subtitle">Edite os textos e a imagem da seção Estúdio na página inicial.</p>
              </div>
            </div>
            {settingsError
              ? <p className="admin-homepage-editor__message is-error" role="alert">{settingsError.message}</p>
              : settingsData
                ? <StudioEditor studio={settingsData.homepage_studio} onSave={handleSaveStudio} />
                : <p className="admin-homepage-editor__loading">Carregando configurações...</p>}
          </>
        )}

        {(isCreatingProject || editingProject) && (
          <ProjectModal
            project={editingProject}
            onClose={() => { setIsCreatingProject(false); setEditingProject(null); }}
            onSave={handleSaveProject}
            onDelete={handleDeleteProject}
          />
        )}

      </main>
    </div>
  );
}
